import objectHash from 'object-hash'
import { computed, type ComputedRef, type Ref, ref, watch } from 'vue'

import type { IMenuItem } from '@/components/common/VMenu.vue'
import { orderRecordsMulti, searchRecords } from '@/components/composables/search'
import type { IHashRecord, IRecord, IStoreEntity } from '@/interfaces'
import { Client, Job, SubJob } from '@/models'
import { AccessLevel, useAuthStore } from '@/stores'

// Type for column filter values - raw values that PrimeTable will convert
export type ColumnFilterValue =
	| string
	| string[]
	| number
	| { from: Date | null; to: Date | null }
	| { min: number | null; max: number | null }

export interface IndexParams {
	[key: string]: unknown
}
export interface IBaseState<T, TI = T> {
	id: string | number
	records: Ref<TI[]>
	activeRecord: Ref<T | null>
	activeRecordSavedHash: Ref<IHashRecord>
	preFilters: Ref<{ [key: string]: string | number | (() => boolean) }>
	searchTerm: Ref<string>
	currentPage: Ref<number>
	sortCriteria: Ref<{ key: string; reverse: boolean }[]>
	pageSize: Ref<number>
	storeActive: Ref<boolean>
	isDuplicating: Ref<boolean>
	indexView: Ref<string>
	indexFilter: Ref<string>
	indexParams: Ref<IndexParams>
	viewList: Ref<IMenuItem[]>
	filterList: Ref<IMenuItem[]>
	// Column filters for PrimeTable integration
	columnFilters: Ref<Record<string, ColumnFilterValue>>
	activeColumnFilters: ComputedRef<Record<string, ColumnFilterValue>>
	hasActiveColumnFilters: ComputedRef<boolean>
	activeColumnFilterCount: ComputedRef<number>
	elevatedPermissions: ComputedRef<boolean>
	currentFormHash: ComputedRef<IHashRecord>
	currentFormDirty: ComputedRef<boolean>
	preFilteredRecords: Ref<TI[]>
	searchedRecords: Ref<TI[]>
	searchRecordIndex: ComputedRef<number>
	searchedRecordLength: ComputedRef<number>
	currentPages: ComputedRef<number>
	totalPages: ComputedRef<number>
	recordCount: ComputedRef<number>
	previousRecord: ComputedRef<TI | undefined>
	nextRecord: ComputedRef<TI | undefined>
	orderedRecords: Ref<TI[]>
	pagedRecords: ComputedRef<TI[]>
}
export interface IBaseActions<T> {
	canRead(level: AccessLevel): ComputedRef<boolean>
	canEdit(level: AccessLevel): ComputedRef<boolean>
	canAdd(level: AccessLevel): ComputedRef<boolean>
	clearActive(): Promise<T>
	clearSearch(): void
	calcHash(record: T): IHashRecord
	// Column filter management methods
	setColumnFilter(columnId: string, value: ColumnFilterValue): void
	clearColumnFilter(columnId: string): void
	clearAllColumnFilters(): void
	getColumnFilter(columnId: string): ColumnFilterValue | undefined
	hasColumnFilter(columnId: string): boolean
}

export interface IBaseStore<T extends IStoreEntity, TI = T> extends IBaseState<T, TI>, IBaseActions<T> {}

export interface IFullActions<T, TI = T> extends IBaseActions<T> {
	getIndex(): Promise<TI[]>
	get(id: string | number): Promise<T>
	save(record: T): Promise<T>
}

export interface IFullStore<T extends IStoreEntity, TI = T>
	extends IBaseState<T, TI>,
		IBaseActions<T>,
		IFullActions<T, TI>,
		IBaseStore<T, TI> {}

// Enhanced store interface with column filtering capabilities
// This interface has been removed because it is equivalent to its supertype, IBaseStore.

export function useBaseStore<T extends IStoreEntity, N extends string, TI extends IRecord = T>(
	moduleId: N
): IBaseStore<T, TI> {
	const moduleName: string = moduleId
	const id: string = ''
	// State
	const records = ref<TI[]>([]) as Ref<TI[]>
	const activeRecord = ref(null) as Ref<T | null>
	const activeRecordSavedHash = ref<IHashRecord>(new Object() as IHashRecord)
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const preFilters = ref<{ [key: string]: string | number | ((...args: any[]) => boolean) }>({})
	const searchTerm = ref<string>('')
	const pageSize = ref<number>(50)
	const currentPage = ref<number>(1)
	const sortCriteria = ref<{ key: string; reverse: boolean }[]>([{ key: 'name', reverse: false }])
	const indexView = ref<string>('table')
	const indexFilter = ref<string>('allrecords')
	const indexParams = ref<IndexParams>({})
	const storeActive = ref<boolean>(false)
	const isDuplicating = ref<boolean>(false)
	const viewList = ref<IMenuItem[]>([])
	const filterList = ref<IMenuItem[]>([])

	// Column filters for PrimeTable integration
	const columnFilters = ref<Record<string, ColumnFilterValue>>({})

	// Getters
	const elevatedPermissions = computed<boolean>(() => {
		if (activeRecord.value instanceof Client) {
			if (!activeRecord.value.salesTeam) {
				return false
			}
			return getAuthStore.value.salesGroup.includes(activeRecord.value.salesTeam)
		} else if (activeRecord.value instanceof Job) {
			if (!activeRecord.value.salesTeam) {
				return false
			}
			return getAuthStore.value.salesGroup.includes(activeRecord.value.salesTeam)
		} else if (activeRecord.value instanceof SubJob) {
			if (!activeRecord.value.salesTeam) {
				return false
			}
			return getAuthStore.value.salesGroup.includes(activeRecord.value.salesTeam)
		} else {
			return false
		}
	})

	const getAuthStore = computed(() => {
		return useAuthStore()
	})

	// Column filter computed properties
	const activeColumnFilters = computed<Record<string, ColumnFilterValue>>(() => {
		// Only return filters that have meaningful values
		const active: Record<string, ColumnFilterValue> = {}
		Object.entries(columnFilters.value).forEach(([key, value]) => {
			if (value !== null && value !== undefined && value !== '') {
				// For arrays, only include if not empty
				if (Array.isArray(value) && value.length === 0) return
				// For date ranges, only include if at least one date is set
				if (typeof value === 'object' && 'from' in value && !value.from && !value.to) return
				// For number ranges, only include if at least one value is set
				if (typeof value === 'object' && 'min' in value && value.min === null && value.max === null) return
				active[key] = value
			}
		})
		return active
	})

	const hasActiveColumnFilters = computed<boolean>(() => {
		return Object.keys(activeColumnFilters.value).length > 0
	})

	const activeColumnFilterCount = computed<number>(() => {
		return Object.keys(activeColumnFilters.value).length
	})

	const currentFormHash = computed<IHashRecord>(() => {
		return { id: 0, hash: objectHash(activeRecord.value, { unorderedArrays: true }) }
	})
	const currentFormDirty = computed<boolean>(() => {
		// Skip dirty check if no saved hash exists or currently duplicating
		if (!activeRecordSavedHash.value.hash || isDuplicating.value) {
			return false
		} else {
			return currentFormHash.value.hash !== activeRecordSavedHash.value.hash
		}
	})

	const preFilteredRecords = ref<TI[]>([]) as Ref<TI[]>
	watch(
		[() => preFilters.value, () => records.value, () => useAuthStore().mySalesTeams],
		() => {
			// console.log('preFilters.value', preFilters.value)
			if (preFilters.value) {
				preFilteredRecords.value = records.value.filter((record) => {
					// Check if there's a 'row' filter and apply it to the whole row
					if (preFilters.value.row && typeof preFilters.value.row === 'function') {
						// If the row filter function returns false, exclude the entire row
						if (!preFilters.value.row(record)) {
							return false
						}
					}
					return Object.keys(preFilters.value).every((key) => {
						const filterValue = preFilters.value[key]
						const recordValue = record[key]

						//Check if the filter value is a function
						if (typeof filterValue === 'function') {
							return filterValue(recordValue)
						}
						// Otherwise, just compare the values directly
						return record[key] === preFilters.value[key]
					})
				})
			} else {
				preFilteredRecords.value = records.value
			}
		},
		{ deep: true }
	)

	const searchedRecords = ref<TI[]>([]) as Ref<TI[]>
	watch(
		[() => searchTerm.value, () => preFilteredRecords.value],
		() => {
			searchedRecords.value = searchRecords(searchTerm.value, preFilteredRecords.value) as TI[]
		},
		{ deep: true }
	)

	const orderedRecords = ref<TI[]>([]) as Ref<TI[]>
	// watch([() => sortKey.value, () => reverse.value, () => searchedRecords.value], () => {
	// 	orderedRecords.value = orderRecords(sortKey.value, reverse.value, searchedRecords.value) as TI[]
	// })
	watch(
		[() => sortCriteria.value, () => searchedRecords.value],
		() => {
			orderedRecords.value = orderRecordsMulti(sortCriteria.value, searchedRecords.value) as TI[]
		},
		{ deep: true }
	)

	const pagedRecords = computed<TI[]>(() => {
		return orderedRecords.value.slice(
			Number(Number(currentPage.value) - 1) * Number(pageSize.value),
			Number(currentPage.value) * Number(pageSize.value)
		)
	})

	const searchRecordIndex = computed<number>(() => {
		if (!activeRecord.value) {
			return -1
		}
		return orderedRecords.value.findIndex((record) => record.id === activeRecord.value?.id)
	})
	const searchedRecordLength = computed<number>(() => {
		return orderedRecords.value.length
	})
	const previousRecord = computed<TI | undefined>(() => {
		if (!activeRecord.value) {
			return undefined
		}
		const index = orderedRecords.value.findIndex((record) => record.id === activeRecord.value?.id)
		return orderedRecords.value[index - 1]
	})
	const nextRecord = computed<TI | undefined>(() => {
		if (!activeRecord.value) {
			return undefined
		}
		const index = orderedRecords.value.findIndex((record) => record.id === activeRecord.value?.id)
		return orderedRecords.value[index + 1]
	})
	const currentPages = computed<number>(() => {
		if (currentPage.value * pageSize.value > searchedRecords.value.length) {
			return searchedRecords.value.length
		} else {
			return currentPage.value * pageSize.value
		}
	})
	const totalPages = computed<number>(() => {
		return Math.ceil(searchedRecords.value.length / pageSize.value)
	})
	const recordCount = computed<number>(() => {
		return searchedRecords.value.length
	})

	// Actions
	function canRead(level: AccessLevel): ComputedRef<boolean> {
		return computed(() => {
			if (elevatedPermissions.value) {
				const maxAccessLevel = Math.max(
					AccessLevel.Expanded,
					getAuthStore.value.getAccessForModuleAndRole(moduleName, 'Read').value
				)
				return level <= maxAccessLevel
			} else {
				return level <= getAuthStore.value.getAccessForModuleAndRole(moduleName, 'Read').value
			}
		})
	}

	function canEdit(level: AccessLevel): ComputedRef<boolean> {
		return computed(() => {
			if (elevatedPermissions.value) {
				const maxAccessLevel = Math.max(
					AccessLevel.Expanded,
					getAuthStore.value.getAccessForModuleAndRole(moduleName, 'Edit').value
				)
				return level <= maxAccessLevel
			} else {
				return level <= getAuthStore.value.getAccessForModuleAndRole(moduleName, 'Edit').value
			}
		})
	}

	function canAdd(level: AccessLevel): ComputedRef<boolean> {
		return computed(() => {
			return level <= getAuthStore.value.getAccessForModuleAndRole(moduleName, 'Add').value
		})
	}

	async function clearActive(): Promise<T> {
		activeRecord.value = {} as T
		activeRecordSavedHash.value = calcHash(activeRecord.value)
		return activeRecord.value
	}

	function clearSearch() {
		searchTerm.value = ''
		currentPage.value = 1
		sortCriteria.value = [{ key: 'name', reverse: false }]
	}

	function calcHash(record: T): IHashRecord {
		return { id: 0, hash: objectHash(record, { unorderedArrays: true }) }
	}

	// Column filter management functions
	function setColumnFilter(columnId: string, value: ColumnFilterValue): void {
		columnFilters.value[columnId] = value
	}

	function clearColumnFilter(columnId: string): void {
		delete columnFilters.value[columnId]
	}

	function clearAllColumnFilters(): void {
		columnFilters.value = {}
	}

	function getColumnFilter(columnId: string): ColumnFilterValue | undefined {
		return columnFilters.value[columnId]
	}

	function hasColumnFilter(columnId: string): boolean {
		const value = columnFilters.value[columnId]
		if (value === null || value === undefined || value === '') return false
		// For arrays, check if not empty
		if (Array.isArray(value)) return value.length > 0
		// For date ranges, check if at least one date is set
		if (typeof value === 'object' && 'from' in value) return Boolean(value.from || value.to)
		// For number ranges, check if at least one value is set
		if (typeof value === 'object' && 'min' in value) return value.min !== null || value.max !== null
		return true
	}

	return {
		id,
		records,
		activeRecord,
		activeRecordSavedHash,
		searchTerm,
		preFilters,
		currentPage,
		sortCriteria,
		storeActive,
		isDuplicating,
		indexView,
		indexFilter,
		indexParams,
		viewList,
		filterList,
		pageSize,

		// Column filters
		columnFilters,
		activeColumnFilters,
		hasActiveColumnFilters,
		activeColumnFilterCount,

		elevatedPermissions,
		currentFormHash,
		currentFormDirty,
		preFilteredRecords,
		searchedRecords,
		searchRecordIndex,
		searchedRecordLength,
		currentPages,
		totalPages,
		recordCount,
		previousRecord,
		nextRecord,
		orderedRecords,
		pagedRecords,

		canRead,
		canEdit,
		canAdd,
		clearActive,
		clearSearch,
		calcHash,

		// Column filter management functions
		setColumnFilter,
		clearColumnFilter,
		clearAllColumnFilters,
		getColumnFilter,
		hasColumnFilter
	}
}
